import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { analytics } from '@mindstudio-ai/interface';
import { useAuth, useAuthModal } from '../state';
import './mobileAuthBar.css';

/**
 * Sign up free / Sign in, always at the bottom of the screen on phones for signed-out visitors on public
 * pages. Light by default, dark while a `data-surface="dark"` section (the landing hero) is behind it.
 * Hidden while signed in (or not yet known), while the auth modal or the menu drawer is open, while the
 * on-screen keyboard is up, and on the sign-in pages. Styles: mobileAuthBar.css (shared with the static blog).
 */
export function MobileAuthBar({ drawerOpen }: { drawerOpen: boolean }) {
  const ready = useAuth((s) => s.ready);
  const user = useAuth((s) => s.user);
  const modalOpen = useAuthModal((s) => s.open);
  const openAuthModal = useAuthModal((s) => s.openModal);
  const [location] = useLocation();
  const [dark, setDark] = useState(false);
  const [keyboard, setKeyboard] = useState(false);
  const bar = useRef<HTMLDivElement>(null);

  const signedOut = ready && !user;
  const onAuthPage = location.startsWith('/signin') || location.startsWith('/signup');
  const hidden = !signedOut || onAuthPage || modalOpen || drawerOpen || keyboard;

  // Page bottom padding and the lifted feedback button follow the bar being shown.
  useEffect(() => {
    const el = document.documentElement;
    el.classList.toggle('has-mab', signedOut && !onAuthPage);
    return () => el.classList.remove('has-mab');
  }, [signedOut, onAuthPage]);

  // The on-screen keyboard: hide the bar so it doesn't ride up over the field being typed in.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const check = () => setKeyboard(vv.height < window.innerHeight * 0.75);
    vv.addEventListener('resize', check);
    return () => vv.removeEventListener('resize', check);
  }, []);

  // Dark look while a dark section sits behind the bar's strip.
  useEffect(() => {
    if (!signedOut) return;
    let io: IntersectionObserver | null = null;
    const setup = () => {
      io?.disconnect();
      const h = bar.current?.offsetHeight || 64;
      const behind = new Set<Element>();
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => (e.isIntersecting ? behind.add(e.target) : behind.delete(e.target)));
          setDark(behind.size > 0);
        },
        { rootMargin: `-${Math.max(0, window.innerHeight - h)}px 0px 0px 0px` },
      );
      document.querySelectorAll('[data-surface="dark"]').forEach((el) => io!.observe(el));
    };
    setup();
    const t = window.setTimeout(setup, 800); // late-mounted sections
    window.addEventListener('resize', setup);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', setup);
      io?.disconnect();
    };
  }, [signedOut, location]);

  if (!signedOut || onAuthPage) return null;
  const go = (mode: 'signin' | 'signup') => (e: React.MouseEvent) => {
    e.preventDefault();
    analytics.track('mab_click', { action: mode, path: location, surface: dark ? 'dark' : 'light' });
    openAuthModal(mode);
  };
  return createPortal(
    <div ref={bar} className={`mab${dark ? ' mab--dark' : ''}${hidden ? ' is-hidden' : ''}`} role="region" aria-label="Get started" aria-hidden={hidden}>
      <a href="/signin" className="mab__in" onClick={go('signin')} tabIndex={hidden ? -1 : 0}>
        Sign in
      </a>
      <a href="/signup" className="mab__up" onClick={go('signup')} tabIndex={hidden ? -1 : 0}>
        Sign up free
      </a>
    </div>,
    document.body,
  );
}
